/**
 * Centralized URL query param parsing for participant identification.
 * Supports both Connect-style params (participantId, assignmentId, projectId)
 * and Prolific-style params (prolificID, prolific-id, etc.) so the app works
 * when launched from either platform.
 */

const PARTICIPANT_ID_KEYS = [
  'participantId',
  'participantid',
  'participant_id',
  'participant-id',
  'prolificID',
  'prolific-id',
  'prolific_id',
  'prolificid',
] as const;

const ASSIGNMENT_ID_KEYS = ['assignmentId', 'assignment_id', 'assignment-id'] as const;
const PROJECT_ID_KEYS = ['projectId', 'project_id', 'project-id'] as const;

function firstNonEmpty(
  params: URLSearchParams,
  keys: readonly string[]
): string | null {
  for (const key of keys) {
    const value = params.get(key);
    if (value != null && value.trim() !== '') return value.trim();
  }
  return null;
}

/**
 * Returns the participant identifier from the URL (Connect participantId or Prolific ID).
 * Checks Connect-style params first, then Prolific-style, so links like
 * ?participantId=XXXXX&assignmentId=XXXXX&projectId=XXXXX work as expected.
 */
export function getParticipantIdFromSearchParams(search: string): string | null {
  const params = new URLSearchParams(search);
  return firstNonEmpty(params, PARTICIPANT_ID_KEYS);
}

/**
 * Returns assignmentId from the URL if present (e.g. Connect).
 */
export function getAssignmentIdFromSearchParams(search: string): string | null {
  const params = new URLSearchParams(search);
  return firstNonEmpty(params, ASSIGNMENT_ID_KEYS);
}

/**
 * Returns projectId from the URL if present (e.g. Connect).
 */
export function getProjectIdFromSearchParams(search: string): string | null {
  const params = new URLSearchParams(search);
  return firstNonEmpty(params, PROJECT_ID_KEYS);
}

/**
 * All Connect/Prolific-style IDs from the current page URL.
 * Useful for sending to the backend or tracking.
 */
export function getParticipantIdsFromSearchParams(search: string): {
  participantId: string | null;
  assignmentId: string | null;
  projectId: string | null;
} {
  return {
    participantId: getParticipantIdFromSearchParams(search),
    assignmentId: getAssignmentIdFromSearchParams(search),
    projectId: getProjectIdFromSearchParams(search),
  };
}
